import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { storeApi, useStore } from "./StoreContext";
import { useDocumentTitle } from "./StorefrontLayout";
import { toast } from "sonner";
import { Check, Truck, MapPin } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Checkout() {
  useDocumentTitle("Checkout", "Complete your order — secure checkout at Furmigo.");
  const { cart, cartTotal, clearCart, customer, requestOtp, verifyOtp } = useStore();
  const nav = useNavigate();
  const [branches, setBranches] = useState([]);
  const [f, setF] = useState({
    customer_name: customer?.name || "",
    customer_mobile: customer?.mobile || "",
    customer_email: "",
    shipping_address: "",
    delivery_branch_id: "",
    payment_method: "cod",
    notes: "",
  });
  const [otp, setOtp] = useState({ shown: false, code: "" });
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);

  useEffect(() => { storeApi.branches().then(setBranches); }, []);
  useEffect(() => { if (!cart.length && !placed) nav("/cart"); }, [cart, placed]); // eslint-disable-line

  const sendOtp = async () => {
    if (!f.customer_mobile) { toast.error("Enter your mobile number"); return; }
    try { await requestOtp(f.customer_mobile); setOtp({ shown: true, code: "" }); toast.success("OTP sent (check WhatsApp)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send OTP"); }
  };
  const checkOtp = async () => {
    try { await verifyOtp(f.customer_mobile, otp.code, f.customer_name); toast.success("Verified"); setOtp({ shown: false, code: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invalid OTP"); }
  };

  const placeOrder = async () => {
    if (!f.customer_name || !f.customer_mobile) { toast.error("Name and mobile required"); return; }
    if (!f.shipping_address) { toast.error("Shipping address required"); return; }
    setBusy(true);
    try {
      const order = await storeApi.checkout({
        ...f,
        items: cart.map(i => ({ product_id: i.product_id, qty: i.qty })),
      });
      setPlaced(order);
      clearCart();
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
    finally { setBusy(false); }
  };

  if (placed) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12 text-center" data-testid="order-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
          <Check className="w-8 h-8" />
        </div>
        <h1 className="font-heading text-3xl font-black mt-4">Order placed!</h1>
        <p className="text-stone-500 mt-1">Thank you {placed.customer_name}. We'll WhatsApp you updates on <span className="font-mono">{placed.customer_mobile}</span>.</p>
        <div className="mt-5 bg-white border border-stone-200 rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-stone-500">Order #</div>
          <div className="font-heading text-2xl font-black">{placed.order_no}</div>
          <div className="text-sm text-stone-500 mt-2">{placed.items.length} items · {inr(placed.grand_total)}</div>
          <div className="text-xs text-stone-400 mt-1">Payment: {placed.payment_method === "cod" ? "Cash on Delivery" : "Paid (mock)"}</div>
        </div>
        <button onClick={() => nav("/")} className="mt-5 bg-stone-900 text-white px-6 py-3 rounded-full font-semibold">Continue shopping</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-10" data-testid="checkout-page">
      <h1 className="font-heading text-3xl font-black mb-4">Checkout</h1>
      <div className="grid sm:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="space-y-3">
          <Section title="Contact">
            <Field label="Name" value={f.customer_name} onChange={v => setF(p => ({ ...p, customer_name: v }))} testid="co-name" />
            <Field label="Mobile (WhatsApp)" value={f.customer_mobile} onChange={v => setF(p => ({ ...p, customer_mobile: v }))} testid="co-mobile" />
            {!customer?.verified && (
              <div className="flex items-center gap-2">
                <button onClick={sendOtp} className="text-xs font-bold text-amber-700 hover:underline" data-testid="co-send-otp">Verify via OTP (optional)</button>
                {otp.shown && (
                  <>
                    <input value={otp.code} onChange={e => setOtp(o => ({ ...o, code: e.target.value }))} placeholder="6-digit code" className="px-2 py-1 border border-stone-300 rounded text-sm w-28" data-testid="co-otp-code" />
                    <button onClick={checkOtp} className="text-xs bg-amber-600 text-white px-3 py-1 rounded font-bold" data-testid="co-verify-otp">Verify</button>
                  </>
                )}
              </div>
            )}
            <Field label="Email (optional)" value={f.customer_email} onChange={v => setF(p => ({ ...p, customer_email: v }))} />
          </Section>
          <Section title={<span className="flex items-center gap-1"><Truck className="w-4 h-4" />Delivery</span>}>
            <Field label="Shipping address" value={f.shipping_address} onChange={v => setF(p => ({ ...p, shipping_address: v }))} multiline testid="co-address" />
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Fulfilling store (optional)</label>
              <select value={f.delivery_branch_id} onChange={e => setF(p => ({ ...p, delivery_branch_id: e.target.value }))} className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-sm">
                <option value="">Auto-pick closest store</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}{b.address ? ` — ${b.address}` : ""}</option>)}
              </select>
            </div>
          </Section>
          <Section title="Payment">
            <label className="flex items-center gap-2 p-3 border border-stone-200 rounded-lg cursor-pointer">
              <input type="radio" checked={f.payment_method === "cod"} onChange={() => setF(p => ({ ...p, payment_method: "cod" }))} />
              <span className="font-semibold text-sm">Cash on Delivery</span>
            </label>
            <label className="flex items-center gap-2 p-3 border border-stone-200 rounded-lg cursor-pointer">
              <input type="radio" checked={f.payment_method === "mock_online"} onChange={() => setF(p => ({ ...p, payment_method: "mock_online" }))} />
              <span className="font-semibold text-sm">Pay online (mock — real payments coming soon)</span>
            </label>
          </Section>
        </div>

        <aside className="bg-white border border-stone-200 rounded-2xl p-4 sm:sticky sm:top-20">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2">Order summary</div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
            {cart.map(l => (
              <div key={l.product_id} className="flex justify-between text-sm">
                <span className="truncate flex-1">{l.name} <span className="text-stone-400">× {l.qty}</span></span>
                <span className="font-mono">{inr(l.qty * l.sale_price)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-200 mt-3 pt-2 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{inr(cartTotal)}</span></div>
            <div className="flex justify-between"><span>Delivery</span><span className="font-mono">Free*</span></div>
            <div className="flex justify-between font-bold text-base pt-1"><span>Total</span><span className="font-mono">{inr(cartTotal)}</span></div>
          </div>
          <button disabled={busy} onClick={placeOrder} className="w-full mt-4 bg-stone-900 disabled:bg-stone-400 text-white py-3.5 rounded-full font-bold" data-testid="place-order-btn">
            {busy ? "Placing…" : "Place order"}
          </button>
          <div className="text-[10px] text-stone-400 mt-2 text-center">By placing this order you agree to our terms. Real payments coming with SuperMoney Breeze.</div>
        </aside>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
    <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{title}</div>
    {children}
  </div>
);
const Field = ({ label, value, onChange, multiline, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</label>
    {multiline ? (
      <textarea value={value} onChange={e => onChange(e.target.value)} className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-sm h-20" data-testid={testid} />
    ) : (
      <input value={value} onChange={e => onChange(e.target.value)} className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg text-sm" data-testid={testid} />
    )}
  </div>
);
